import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IApiChannel, ChannelName, ChannelType } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import { mapChannelTypeToName } from '../../services/channels.service';
import {
  ChannelCardContainer,
  ChannelCardHeader,
  ChannelLogos,
  MsoolLogo as MsoolLogoStyled,
  ConnectionLine,
  ChannelLogo as ChannelLogoStyled,
  StatusBadge,
  StatusDot,
  ChannelStats,
  StatItem,
  StatDivider,
  ConnectButton,
} from '../../styles';

// Import unified channel logo components
import { ChannelLogo, MsoolLogo, ChannelLogoType } from '../ChannelLogos';

interface IChannelCardProps {
  channel: IApiChannel;
  onConnect: (channelName: ChannelName) => void;
  onManage?: (channelName: ChannelName) => void;
  disabled?: boolean;
  disabledMessage?: string;
}

const ChannelCard: FC<IChannelCardProps> = memo(({ channel, onConnect, onManage, disabled, disabledMessage }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Derive channel name from channelType
  const channelName = useMemo(() => mapChannelTypeToName(channel.channelType), [channel.channelType]);
  
  // Derive status from isConnected
  const status = channel.isConnected ? 'connected' : 'not_connected';

  const getStatusText = () => {
    if (channel.isConnected) {
      return t(T_KEYS.CONNECTED);
    }
    return t(T_KEYS.NOT_CONNECTED);
  };

  // Map ChannelType to ChannelLogoType
  const getChannelLogoType = (): ChannelLogoType | null => {
    switch (channel.channelType) {
      case ChannelType.Airbnb:
        return 'airbnb';
      case ChannelType.Booking:
        return 'booking';
      case ChannelType.Gathern:
        return 'gathern';
      default:
        return null;
    }
  };

  const channelLogoType = getChannelLogoType();

  const handleButtonClick = () => {
    if (disabled) return;
    if ((channel.connectedAccounts?.length ?? 0) && onManage) {
      onManage(channelName);
    } else {
      onConnect(channelName);
    }
  };

  const channelInfo = CHANNEL_INFO[channelName];

  // Determine button text
  const getButtonText = () => {
    if (disabled && disabledMessage) {
      return disabledMessage;
    }
    if ((channel.connectedAccounts?.length ?? 0) > 0) {
      return t(T_KEYS.MANAGE_ACCOUNTS);
    }
    return `${t(T_KEYS.CONNECT_WITH)} ${isRtl ? channelInfo.displayNameAr : channelInfo.displayName}`;
  };

  return (
    <ChannelCardContainer $disabled={disabled}>
      <ChannelCardHeader>
        <ChannelLogos>
          {channelLogoType && (
            <ChannelLogo channel={channelLogoType} size="xl" />
          )}
          <ConnectionLine />
          <MsoolLogo size="xl" />
        </ChannelLogos>
      </ChannelCardHeader>
      <div className='w-full flex justify-center'>
        <StatusBadge $status={status}>
          <StatusDot $status={status} />
          {getStatusText()}
        </StatusBadge>
      </div>
      <div className=''>
        <ChannelStats>
          <StatItem>
            {channel.numberOfConnectedAccounts} {t(T_KEYS.CONNECTED_ACCOUNTS)}
          </StatItem>
          <StatDivider>•</StatDivider>
          <StatItem>
            {channel.importedUnits} {t(T_KEYS.IMPORTED_UNITS)}
          </StatItem>
        </ChannelStats>
        {channel.numberOfDisconnectedAccounts > 0 && (
          <StatItem style={{ color: '#f04438', textAlign: 'center' }}>
            {channel.numberOfDisconnectedAccounts} {t(T_KEYS.DISCONNECTED_ACCOUNTS)}
          </StatItem>
        )}
      </div>
      <ConnectButton
        $status={(channel.connectedAccounts?.length ?? 0) > 0 ? 'connected' : 'not_connected'}
        onClick={handleButtonClick}
        disabled={disabled}
        $disabled={disabled}
      >
        {getButtonText()}
      </ConnectButton>
    </ChannelCardContainer>
  );
});

ChannelCard.displayName = 'ChannelCard';

export default ChannelCard;
